import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { first, Subject, switchMap, takeUntil } from 'rxjs';

import { ApiReplaceSubjectCategoryRequest, ApiSubjectCategory, ApiSubjectCategoryList, ApiThema, ProjectSubjectCategoriesClient, SubjectCategoriesClient, SubjectCategoryType } from 'content-manager/src/app/core/open-api/content-manager-api-client';
import { ProjectStateService } from 'content-manager/src/app/projects/project/project-state.service';
import { TableSettings } from 'sc-common';

@Component({
    selector: 'cm-project-level-themas',
    templateUrl: './project-level-themas.component.html',
    standalone: false
})
export class ProjectLevelThemasComponent implements OnInit, OnDestroy {

    @Input({ required: true }) public isReadonly = false;

    public readonly tableSettings: TableSettings<ApiSubjectCategoryList>;

    public selectedThemaCodes: string[] = [];

    public requestInProcess = false;

    private _originalSelectedThemaCodes: string[] = [];

    private readonly _refreshTable$ = new Subject<void>();

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private readonly _projectStateService: ProjectStateService,
        private readonly _subjectCategoriesClient: SubjectCategoriesClient,
        private readonly _projectSubjectCategoriesClient: ProjectSubjectCategoriesClient) {

        this.tableSettings = this._initTable();
    }

    public get hasChanges(): boolean {

        if (this._originalSelectedThemaCodes.length !== this.selectedThemaCodes.length) {
            return true;
        }

        const sortedOriginal = [...this._originalSelectedThemaCodes].sort();
        const sortedCurrent = [...this.selectedThemaCodes].sort();

        return sortedOriginal.some((v, i) => v !== sortedCurrent[i]);
    }

    public get canUpdate(): boolean {
        return !this.isReadonly && this.hasChanges && !this.requestInProcess;
    }

    public ngOnInit(): void {
        this._loadSelection();
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._refreshTable$.complete();
    }

    public update(): void {

        this.requestInProcess = true;

        this._subjectCategoriesClient.getThemas().pipe(
            first(),
            switchMap(themas => this._projectStateService.firstSwitchMap(project =>
                this._projectSubjectCategoriesClient.replaceSubjectCategories(
                    project.id,
                    undefined,
                    new ApiReplaceSubjectCategoryRequest({
                        subjectCategoryType: SubjectCategoryType.Thema,
                        subjectCategories: this._buildSubjectCategories(themas)
                    })))),
            takeUntil(this._destroy$))
            .subscribe(() => {
                this.requestInProcess = false;
                this._originalSelectedThemaCodes = [...this.selectedThemaCodes];
                this._refreshTable$.next();
            });
    }

    private _loadSelection(): void {
        this._projectStateService.firstSwitchMap(project =>
            this._projectSubjectCategoriesClient.getSubjectCategoryList(project.id, SubjectCategoryType.Thema, undefined))
            .pipe(takeUntil(this._destroy$))
            .subscribe(result => {
                const codes = result.items?.map(c => c.code!).filter(c => !!c) ?? [];
                this.selectedThemaCodes = [...codes];
                this._originalSelectedThemaCodes = [...codes];
            });
    }

    private _buildSubjectCategories(themas: ApiThema[]): ApiSubjectCategory[] {
        return this.selectedThemaCodes
            .map(code => themas.find(t => t.code === code))
            .filter((t): t is ApiThema => !!t)
            .map(thema => new ApiSubjectCategory({
                subjectCategoryType: SubjectCategoryType.Thema,
                code: thema.code,
                headingText: thema.heading
            }));
    }

    private _initTable(): TableSettings<ApiSubjectCategoryList> {
        return TableSettings.create(ApiSubjectCategoryList, {
            dataSource: oDataQuery => this._projectStateService.switchMap(project =>
                this._projectSubjectCategoriesClient.getSubjectCategoryList(project.id, SubjectCategoryType.Thema, oDataQuery)),
            columnResizeMode: 'fit',
            paginator: false,
            emptyMessageText: $localize`No themas assigned`,
            reload$: this._refreshTable$.asObservable(),
            columns: {
                code: {
                    header: $localize`Code`,
                    width: '120px'
                },
                headingText: {
                    header: $localize`Thema`
                }
            },
            hasFilters: false,
            defaultSort: {
                field: x => x.code
            }
        });
    }
}
